/** Every route the application serves. */

import type { ReactElement } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@/app/layout/AppShell";
import { RequireSession } from "@/features/auth/session";
import { LoginPage } from "@/features/auth/LoginPage";
import { DashboardPage } from "@/features/cases/DashboardPage";
import { CasesPage } from "@/features/cases/CasesPage";
import { CaseWorkspace } from "@/features/cases/CaseWorkspace";
import { ReviewInboxPage } from "@/features/review/ReviewInboxPage";
import { PluginsPage } from "@/features/plugins/PluginsPage";
import { ApiKeysPage } from "@/features/security/ApiKeysPage";
import { MfaPage } from "@/features/security/MfaPage";
import { AdminPage } from "@/features/admin/AdminPage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { AuditPage } from "@/features/audit/AuditPage";
import { SearchPage } from "@/features/search/SearchPage";
import { CompliancePage } from "@/features/compliance/CompliancePage";
import { PeopleResearchPage } from "@/features/people/PeopleResearchPage";
import { SubjectWorkspace } from "@/features/people/SubjectWorkspace";

/** The route table. */
export function AppRoutes(): ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <RequireSession>
            <AppShell />
          </RequireSession>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="cases" element={<CasesPage />} />
        <Route path="cases/:caseId/*" element={<CaseWorkspace />} />
        <Route path="people" element={<PeopleResearchPage />} />
        {/* A search has an address. The results are in the database either way; without
            this route the run id lived in component state, so changing page lost the only
            handle to them — and a research session never appears in the case list, so
            nothing else could reach them. Re-running was the only way back. */}
        <Route path="people/searches/:runId" element={<PeopleResearchPage />} />
        {/* Splat, as `cases/:caseId/*` is: the workspace owns its own panel routes, so
            every panel keeps a bookmarkable address. */}
        <Route path="people/subjects/:subjectId/*" element={<SubjectWorkspace />} />
        <Route path="review" element={<ReviewInboxPage />} />
        <Route path="plugins" element={<PluginsPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
        <Route path="mfa" element={<MfaPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="compliance" element={<CompliancePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
